import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BellOutlined,
  CalendarOutlined,
  CheckOutlined,
  ExpandAltOutlined,
  MessageOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Button, Empty, Grid, Popover, Spin } from "antd";
import { getNotificationGroup, isNotificationRead, NotificationGroup } from "../../../../constances/NotificationsConstance";
import { getNotificationActorAvatar, useNotificationActorAvatars } from "../../../../hooks/useNotificationActorAvatars";
import { useMarkAllRead, useMarkRead, useNotifications, useUnreadCount } from "../../../../hooks/useNotifications";
import { NotificationResponseDto } from "../../../../models/notification/NotificationResponseDto";
import { navigateNotificationLink } from "../../../../utils/NotificationNavigation";
import { CommonAvatar } from "../avatar/CommonAvatar";
import { CommonBadge } from "../badge/CommonBadge";

interface NotificationBellProps {
  tone?: "light" | "dark";
}

type NotificationView = "ALL" | "UNREAD";

const VIEW_OPTIONS: Array<{ label: string; value: NotificationView }> = [
  { label: "All", value: "ALL" },
  { label: "Unread", value: "UNREAD" },
];

const formatDate = (iso?: string) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "2-digit", year: "numeric" });
  } catch {
    return "";
  }
};

const formatTime = (iso?: string) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};

const getSectionLabel = (iso?: string) => {
  if (!iso) return "Earlier";

  const date = new Date(iso);
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);

  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  if (sameDay(date, today)) return "Today";
  if (sameDay(date, tomorrow)) return "Tomorrow";
  return "Earlier";
};

const getIcon = (group: NotificationGroup, notification: NotificationResponseDto) => {
  if (notification.actorName) return undefined;
  if (group === "COMMENT") return <MessageOutlined />;
  if (group === "SHIPMENT") return <CalendarOutlined />;
  if (group === "APPROVAL") return <CheckOutlined />;
  return <WarningOutlined />;
};

const getAvatarClassName = (group: NotificationGroup, read: boolean) => {
  if (read) return "bg-[#f1f5f9] text-[#789]";
  if (group === "COMMENT") return "bg-[#e7f4f1] text-[#0f766e]";
  if (group === "SHIPMENT") return "bg-[#e8f4ff] text-[#1677ff]";
  if (group === "APPROVAL") return "bg-[#ecfdf3] text-[#15803d]";
  return "bg-[#e7f4f1] text-[#0f766e]";
};

const NotificationRow = ({
  item,
  avatarSrc,
  onClick,
}: {
  item: NotificationResponseDto;
  avatarSrc?: string;
  onClick: (item: NotificationResponseDto) => void;
}) => {
  const read = isNotificationRead(item);
  const group = getNotificationGroup(item);
  const icon = avatarSrc ? undefined : getIcon(group, item);

  return (
    <button
      type="button"
      onClick={() => onClick(item)}
      className="group flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-[#f7fafc] rounded-none border-none outline-none cursor-pointer"
    >
      <CommonAvatar
        size={36}
        src={avatarSrc}
        name={item.actorName || item.title || "Notification"}
        className={`shrink-0 ${avatarSrc ? "bg-transparent" : getAvatarClassName(group, read)}`}
        icon={icon}
      />

      <div className="min-w-0 flex-1">
        <div className="truncate text-[12px] font-semibold leading-5 text-[#111827]">
          {item.title || item.content || "Notification"}
        </div>
        <div className="mt-0.5 flex min-w-0 items-center gap-2 text-[11px] leading-4 text-[#8a8f98]">
          <span className="truncate">{formatDate(item.registeredTime)}</span>
          <span className="h-3 w-px shrink-0 bg-[#d9dee7]" />
          <span className="shrink-0">{formatTime(item.registeredTime)}</span>
        </div>
      </div>

      {!read && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#e11d48]" />}
    </button>
  );
};

export const NotificationBell = ({ tone = "light" }: NotificationBellProps) => {
  const screens = Grid.useBreakpoint();
  const popoverWidth = screens.sm ? 424 : 256;
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<NotificationView>("ALL");
  const navigate = useNavigate();

  const { data: unreadCount = 0 } = useUnreadCount();
  const { data: page, isLoading } = useNotifications(0, 20, open, view === "UNREAD" ? { isRead: false } : undefined);
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();

  const items = useMemo(() => page?.content ?? [], [page]);
  const avatarByActorId = useNotificationActorAvatars(items);
  const groupedItems = useMemo(() => {
    const groups: Array<{ label: string; items: NotificationResponseDto[] }> = [];
    items.forEach((item) => {
      const label = getSectionLabel(item.registeredTime);
      const existing = groups.find((group) => group.label === label);
      if (existing) {
        existing.items.push(item);
      } else {
        groups.push({ label, items: [item] });
      }
    });
    return groups;
  }, [items]);

  const click = (notification: NotificationResponseDto) => {
    if (!isNotificationRead(notification)) {
      markRead.mutate(notification.id);
    }

    setOpen(false);

    navigateNotificationLink(navigate, notification.link);
  };

  const content = (
    <div className="w-full overflow-hidden rounded-none bg-white text-[12px] shadow-[0_18px_48px_rgba(15,23,42,0.18)]">
      <div className="flex items-start justify-between border-b border-[#eef1f5] px-4 py-4">
        <div className="min-w-0">
          <div className="text-[18px] font-semibold leading-6 text-[#0b1220]">Notifications</div>
          <div className="mt-1 text-[13px] leading-5 text-[#7b8190]">Stay Updated with Your Latest Notifications</div>
        </div>

        <div className="ml-3 flex shrink-0 items-center gap-2 text-[#6b7280]">
          <button
            type="button"
            aria-label="Open notifications page"
            className="flex h-7 w-7 items-center justify-center rounded-none border-none bg-transparent hover:bg-[#f3f5f8] outline-none cursor-pointer"
            onClick={() => {
              navigate("/notifications");
              setOpen(false);
            }}
          >
            <ExpandAltOutlined className="text-[13px]" />
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between border-b border-[#eef1f5] bg-[#f7f8fa] px-4 py-2">
        <div className="flex items-center gap-3">
          {VIEW_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setView(option.value)}
              className={[
                "border-none bg-transparent p-0 text-[12px] leading-5 transition-colors outline-none cursor-pointer rounded-none",
                view === option.value ? "font-semibold text-[#063f3c]" : "font-medium text-[#111827] hover:text-[#063f3c]",
              ].join(" ")}
            >
              {option.label}
              {option.value === "UNREAD" && unreadCount > 0 ? ` (${unreadCount})` : ""}
            </button>
          ))}
        </div>

        {unreadCount > 0 && (
          <Button
            type="text"
            size="small"
            icon={<CheckOutlined />}
            className="h-7 px-1 text-[12px] font-medium text-[#063f3c] !rounded-none"
            loading={markAllRead.isPending}
            onClick={() => markAllRead.mutate()}
          >
            Mark all as read
          </Button>
        )}
      </div>

      <div className="max-h-[calc(100dvh-220px)] overflow-y-auto bg-white">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Spin />
          </div>
        ) : groupedItems.length === 0 ? (
          <div className="py-12">
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No notifications" />
          </div>
        ) : (
          groupedItems.map((group) => (
            <div key={group.label}>
              <div className="px-4 pb-1 pt-4 text-[12px] font-medium leading-5 text-[#7b8190]">{group.label}</div>
              {group.items.map((item) => (
                <NotificationRow key={item.id} item={item} avatarSrc={getNotificationActorAvatar(item, avatarByActorId)} onClick={click} />
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
      overlayStyle={{
        width: popoverWidth,
        maxWidth: popoverWidth,
      }}
      overlayInnerStyle={{
        padding: 0,
        borderRadius: 0,
        overflow: "hidden",
      }}
    >
      <button
        type="button"
        aria-label="Notifications"
        className={[
          "box-border inline-flex h-[26px] w-[26px] min-w-[26px] max-w-[26px] items-center justify-center p-0 shrink-0 border-none rounded-none leading-none cursor-pointer outline-none transition-colors",
          tone === "light"
            ? "bg-white/10 text-white hover:bg-white/20"
            : "bg-transparent text-neutral-700 hover:bg-neutral-100",
        ].join(" ")}
      >
        <CommonBadge count={unreadCount} size="small" overflowCount={99}>
          <BellOutlined
            style={{
              fontSize: 17,
              color: tone === "light" ? "#fff" : undefined,
            }}
          />
        </CommonBadge>
      </button>
    </Popover>
  );
};
